import { getCustomCssProperty } from '@craft-kit/helper/general';
import { createCoopIcon } from './coop-icon';

export const ROOT_CLASS = 'accordion';
const BUTTON_CLASS = `${ROOT_CLASS}__button`;
export const BLOCK_CLASS = `${ROOT_CLASS}__block`;
const CONTENT_CLASS = `${ROOT_CLASS}__content`;
const SEARCH_INPUT_CLASS = `${ROOT_CLASS}__search-input`;
const CHIP_CONTAINER_CLASS = `${ROOT_CLASS}__chip-container`;
const SELECTED_FILTER_LABEL_CLASS = `${ROOT_CLASS}__selected-filter-label`;
const BLOCK_CLASS_BEFORE_VISIBLE = `${BLOCK_CLASS}--before-visible`;
const BLOCK_CLASS_AFTER_VISIBLE = `${BLOCK_CLASS}--after-visible`;
const BLOCK_CLASS_VISIBLE = `${BLOCK_CLASS}--visible`;
const CLASS_APPEND_TIME = 30;
let TRANSITION_DURATION: number = 300;

export type AccordionEntry = {
  id: number,
  index: number,
  answer: string,
  question: string
}

export const init = (rootEl: HTMLElement) => {
  TRANSITION_DURATION = parseInt(getCustomCssProperty(rootEl, '--transition-duration'));
  const shouldMultipleBlockOpen = rootEl.dataset.multipleBlockOpen === '1';
  initAccordionButtons(rootEl, shouldMultipleBlockOpen);
  const searchInputs = rootEl.querySelectorAll<HTMLInputElement>(`.${SEARCH_INPUT_CLASS}`);
  const blocks = rootEl.querySelectorAll<HTMLDivElement>(`.${BLOCK_CLASS}`);

  searchInputs.forEach((searchInput) => {
    searchInput.addEventListener('input', () => {
      const blockEl = searchInput.closest<HTMLDivElement>(`.${CONTENT_CLASS}`);

      const searchValue = searchInput.value.toLowerCase();
      const chipContainerEl = blockEl?.querySelector<HTMLDivElement>(`.${CHIP_CONTAINER_CLASS}`);
      const items = chipContainerEl?.querySelectorAll<HTMLDivElement>('.chip');

      if (items) {
        items.forEach((item) => {
          const text = item.textContent ?? '';
          if (text.toLowerCase().indexOf(searchValue) > -1) {
            item.removeAttribute('style');
          } else {
            item.style.display = 'none';
          }
        });
      }
    });
  });

  if (searchInputs) {
    blocks.forEach((block) => {
      const contentEl = getContentOfBlock(block);
      contentEl?.querySelectorAll<HTMLLabelElement>('.chip').forEach((label) => {
        initChipChangeEvent(label, contentEl);
      });
    });

    setTimeout(() => {
      blocks.forEach((block) => {
        preselectChipsOfBlock(block);
      });
    }, CLASS_APPEND_TIME);
  }
};

export const initAccordionButtons = (rootEl: HTMLElement, shouldMultipleBlockOpen: boolean = true) => {
  const blocks = rootEl.querySelectorAll<HTMLDivElement>(`.${BLOCK_CLASS}`);
  const buttons = rootEl.querySelectorAll<HTMLButtonElement>(`.${BUTTON_CLASS}`);
  buttons.forEach((button, index) => {
    const block = blocks[index];

    button.addEventListener('click', () => {
      const isVisible = block.classList.contains(BLOCK_CLASS_VISIBLE);

      closeAllAccordions(blocks, block, shouldMultipleBlockOpen, () => {
        if (isVisible) {
          closeAccordionContent(block);
        } else {
          showAccordionContent(block);
        }
      });
    });

    button.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') {
        closeAccordionContent(block);
      }
    });

    if (button.getAttribute('aria-expanded') === 'true') {
      showAccordionContent(block);
    }
  });
};

const preselectChipsOfBlock = (block: HTMLElement) => {
  const contentEl = getContentOfBlock(block);
  const searchInput = contentEl?.querySelector<HTMLInputElement>(`.${SEARCH_INPUT_CLASS}`);
  const chipContainerEl = contentEl?.querySelector<HTMLDivElement>(`.${CHIP_CONTAINER_CLASS}`);
  if (searchInput && chipContainerEl) {
    chipContainerEl.querySelectorAll<HTMLLabelElement>('.chip').forEach((label) => {
      if (label.querySelector<HTMLInputElement>('input')?.checked) {
        const currentSearchInput = contentEl?.querySelector<HTMLInputElement>(`.${SEARCH_INPUT_CLASS}`);
        if (!currentSearchInput) {
          return;
        }
        currentSearchInput.parentNode?.insertBefore(label, currentSearchInput.nextSibling); // move label to the top of search input
      }
    });
  }
}

const initChipChangeEvent = (label: HTMLLabelElement, contentEl: HTMLDivElement) => {
  label.addEventListener('change', () => {
    const isChecked = label.querySelector<HTMLInputElement>('input')?.checked;
    if (isChecked) {
      const currentSearchInput = contentEl?.querySelector<HTMLInputElement>(`.${SEARCH_INPUT_CLASS}`);
      if (!currentSearchInput) {
        return;
      }
      currentSearchInput.parentNode?.insertBefore(label, currentSearchInput.nextSibling); // move label to the top of search input
    } else {
      const chipContainerEl = contentEl?.querySelector<HTMLDivElement>(`.${CHIP_CONTAINER_CLASS}`);
      chipContainerEl?.appendChild(label);
      sortChipLabels(chipContainerEl);
    }
  });
};

const sortChipLabels = (chipContainerEl: HTMLDivElement | null) => {
  if (chipContainerEl) {
    const chipLabelList = Array.from(chipContainerEl.querySelectorAll<HTMLDivElement>('.chip'));

    const chipArraySorted = chipLabelList.sort((a, b) =>
      (a.textContent ?? '').localeCompare(b.textContent ?? ''));

    const getChipBefore = (chip: HTMLDivElement) => {
      const chipIndex = chipArraySorted.indexOf(chip);
      return chipIndex > 0 ? chipArraySorted[chipIndex - 1] : null;
    }

    chipArraySorted.forEach((chip) => {
      const chipBefore = getChipBefore(chip);
      if (chipBefore) {
        chipContainerEl.insertBefore(chip, chipBefore.nextSibling);
      }
    });
  }
};

const closeAccordionContent = (block: HTMLElement, callback?: () => void) => {
  if (!isBlockVisible(block)) {
    // this block is already closed
    return;
  }

  if (block.classList.contains(BLOCK_CLASS_AFTER_VISIBLE)) {
    // this block is already closing
    return;
  }

  if (block.classList.contains(BLOCK_CLASS_BEFORE_VISIBLE)) {
    // this block is opening, so we need to close it after opening
    setTimeout(() => {
      closeAccordionContent(block);
    }, (TRANSITION_DURATION + CLASS_APPEND_TIME) * 2);
    return;
  }

  const currentButton = block.querySelector<HTMLButtonElement>(`.${BUTTON_CLASS}`);
  if (currentButton) {
    currentButton.setAttribute('aria-expanded', 'false');
  }

  const contentEl = getContentOfBlock(block);

  if (!contentEl) {
    return;
  }

  const contentHeight = contentEl.scrollHeight;

  contentEl.style.setProperty('--content-height', `${contentHeight}px`);
  block.classList.add(BLOCK_CLASS_AFTER_VISIBLE);
  block.classList.remove(BLOCK_CLASS_VISIBLE);

  setTimeout(() => {
    contentEl.style.setProperty('--content-height', '0');
  }, CLASS_APPEND_TIME);

  setTimeout(() => {
    block.classList.remove(BLOCK_CLASS_AFTER_VISIBLE);
    contentEl.style.removeProperty('--content-height');
    callback && callback();
  }, TRANSITION_DURATION + CLASS_APPEND_TIME);

};

const showAccordionContent = (block: HTMLElement) => {
  const currentButton = block.querySelector<HTMLButtonElement>(`.${BUTTON_CLASS}`);
  if (currentButton) {
    currentButton.setAttribute('aria-expanded', 'true');
  }

  const contentEl = getContentOfBlock(block);

  if (!contentEl) {
    return;
  }

  contentEl.style.setProperty('--content-height', `${contentEl.scrollHeight}px`);
  block.classList.add(BLOCK_CLASS_BEFORE_VISIBLE);

  setTimeout(() => {
    contentEl.style.setProperty('--content-height', `${contentEl.scrollHeight}px`);

  }, CLASS_APPEND_TIME);

  setTimeout(() => {
    block.classList.add(BLOCK_CLASS_VISIBLE);
    block.classList.remove(BLOCK_CLASS_BEFORE_VISIBLE);
    contentEl.style.removeProperty('--content-height');
    const blockHeader = block.querySelector<HTMLDivElement>(`.${BUTTON_CLASS}`);
    if (blockHeader) {
      const yOffset = -80;
      const top = blockHeader.getBoundingClientRect().top + document.documentElement.scrollTop + yOffset;
      window.scrollTo({top, behavior: 'smooth'});
    }
  }, TRANSITION_DURATION + CLASS_APPEND_TIME);
};

const closeAllAccordions = (blocks: NodeListOf<HTMLElement>, currentBlock: HTMLElement, shouldMultipleBlockOpen: boolean, callback: () => void) => {
  blocks.forEach((block) => {
    if (block !== currentBlock && !shouldMultipleBlockOpen) {
      closeAccordionContent(block, () => callback && callback());
    } else {
      callback && callback();
    }
  });
};

const isBlockVisible = (block: HTMLElement): boolean =>
  block.classList.contains(BLOCK_CLASS_VISIBLE)
  || block.classList.contains(BLOCK_CLASS_BEFORE_VISIBLE)
  || block.classList.contains(BLOCK_CLASS_AFTER_VISIBLE);

const getContentOfBlock = (block: HTMLElement): HTMLDivElement|null => block.querySelector(`.${CONTENT_CLASS}`);

export const updateAccordionFilterLabel = (rootEl: HTMLElement, accordionNameList: string[], allFilterLabel: HTMLElement): number => {
  let allFilters = 0;
  accordionNameList.forEach((accordionName) => {
    allFilters += addAccordionFilterLabel(rootEl, `${accordionName}[]`);
  });

  setAllFilterLabel(allFilterLabel, allFilters);

  return allFilters;
};

const setAllFilterLabel = (allFilterLabel: HTMLElement, count: number) => {
  if (allFilterLabel) {
    allFilterLabel.dataset.filterCount = count.toString();
    allFilterLabel.textContent = count > 0 ? count.toString() : '';
  }
};

export const addAccordionFilterLabel = (rootEl: HTMLElement, filterName: string): number => {
  const filterSelected = rootEl.querySelectorAll<HTMLInputElement>(`input[name="${filterName}"]:checked`);
  if (filterSelected.length === 0) {
    const filter = rootEl.querySelectorAll<HTMLInputElement>(`input[name="${filterName}"]`);

    if (filter && filter.length > 0) {
      const accordionBlockOfFilter = filter[0].closest<HTMLDivElement>(`.${BLOCK_CLASS}`);
      if (accordionBlockOfFilter) {
        const filterLabel = accordionBlockOfFilter.querySelector<HTMLSpanElement>(`.${SELECTED_FILTER_LABEL_CLASS}`);
        if (filterLabel) {
          filterLabel.textContent = '';
        }
      }
    }

    return 0;
  }

  const accordionBlockOfFilter = filterSelected[0].closest<HTMLDivElement>(`.${BLOCK_CLASS}`);
  if (accordionBlockOfFilter) {
    const filterLabel = accordionBlockOfFilter.querySelector<HTMLSpanElement>(`.${SELECTED_FILTER_LABEL_CLASS}`);

    if (filterLabel) {
      filterLabel.textContent = filterSelected.length.toString();
    }

    return filterSelected.length;
  }

  return filterSelected ? filterSelected.length : 0;
};

export const createAccordionCard = (accordionEntry: AccordionEntry) => {
  const domParser = new DOMParser();
  const blockId = `basicsHeading${accordionEntry.id}_${accordionEntry.index}`;
  const collapseId = `${blockId}--collapse`;
  const accordionItem =`
    <div class="${BLOCK_CLASS}">
      <h4 class="accordion__header" id="${blockId}">
        <button
            class="accordion__button"
            type="button"
            aria-expanded="false"
            aria-controls="${collapseId}"
        >
          <div class="accordion__button-text">
            <span class="accordion__button-text-headline">${accordionEntry.question}</span>
          </div>
          <div class="accordion__button-icon button button--icon-only">${createCoopIcon('close')}</div>
        </button>
      </h4>
      <div id="${collapseId}" class="accordion__content" aria-labelledby="${blockId}">
        <div class="accordion__content-inner">
          <div class="richtext">${accordionEntry.answer}</div>
        </div>
      </div>
    </div>`;

  const itemDom = domParser.parseFromString(accordionItem, 'text/html');
  const itemEl = itemDom.querySelector<HTMLDivElement>(`.${BLOCK_CLASS}`);

  return itemEl;
}
